import classNames from "classnames";
import React, { FunctionComponent } from "react";
import { useIntl } from "react-intl";
import { FilterTag, ORDERED_FILTER_TAGS } from "../../../../../agreed/filters";
import useCustomizationContext from "../../../../../hooks/useCustomizationContext";
import { ReactComponent as IconCheck } from "../../../../../icons/check.svg";
import { EditedPicture, Panel } from "../../../../../states/customization";
import { sizeP10kToMm } from "../../../../../utils/customization/box";
import { calcNextCropping } from "../../../../../utils/customization/cropping";
import {
  findEditablePictureDefinition,
  findPageAndDefinition,
} from "../../../../../utils/customization/finders";
import { findPhoto } from "../../../../../utils/selection/finders";
import { imageUrlFromSelectionPhoto } from "../../../../../utils/selection/photo";
import EditablePictureSvg from "../../../../atoms/customization/EditablePictureSvg";
import PanelClosing from "../../../../molecules/customization/PanelClosing";
import "./style.scss";

interface Props {
  editedPicture: EditedPicture;
}

const FilterPanel: FunctionComponent<Props> = ({ editedPicture }) => {
  const intl = useIntl();
  const { content, dispatch, selection, template } = useCustomizationContext();

  const { page, pageDefinition } = findPageAndDefinition(editedPicture.pageKey, template, content);
  const elementDefinition = findEditablePictureDefinition(
    editedPicture.editablePictureKey,
    pageDefinition
  );

  const panelClosingOnClickHandler = () => {
    dispatch({ type: "setPanel", panel: Panel.CLOSED });
  };

  const filterOnClickHandler = (filterTag: FilterTag) => {
    dispatch({
      editablePictureKey: editedPicture.editablePictureKey,
      filterTag,
      pageKey: editedPicture.pageKey,
      type: "changeEditablePictureElementFilter",
      undoKey: `PictureEditor-${editedPicture.pageKey}`,
    });
  };

  const element = page.editablePictures.find(
    (editablePicture) => editablePicture.editablePictureKey === editedPicture.editablePictureKey
  );
  if (!element) {
    panelClosingOnClickHandler();
    return null;
  }
  const elementSizeMm = sizeP10kToMm(elementDefinition, pageDefinition);

  const selectionPhoto = findPhoto(element.selectionPhotoKey, selection);
  const croppingRatio = elementSizeMm.width.value / elementSizeMm.height.value;
  const imageUrlForFilters = imageUrlFromSelectionPhoto(selectionPhoto, "thumbnail");

  const croppingForFilters = calcNextCropping({
    nextRatio: 1,
    prevCropping: element.cropping,
    prevRatio: croppingRatio,
  });

  return (
    <div className="FilterPanel">
      <PanelClosing onClick={panelClosingOnClickHandler}>
        {intl.formatMessage({ id: "kustom_PictureEditor_filters" })}
      </PanelClosing>
      <div className="FilterPanel-content">
        {ORDERED_FILTER_TAGS.map((itemFilterTag: FilterTag) => {
          const isSelected = itemFilterTag === element.filterTag;

          return (
            <div
              key={itemFilterTag}
              className={classNames("FilterPanel-content-filterItem", {
                "FilterPanel-content-filterItem--selected": isSelected,
              })}
              onClick={() => filterOnClickHandler(itemFilterTag)}
            >
              <EditablePictureSvg
                cropping={croppingForFilters}
                filterTag={itemFilterTag}
                heightPx={100}
                imageUrl={imageUrlForFilters}
                widthPx={100}
              />
              {isSelected && (
                <div className="FilterPanel-content-filterItem-selection">
                  <IconCheck />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FilterPanel;
